import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Building2, Users, DollarSign, Star, LogOut, Home, Clock, CheckCircle, XCircle, Database, Settings } from 'lucide-react';

interface AccessStatus {
  hasAccess: boolean;
  status: string;
  role?: string;
  message: string;
}

interface Customer {
  id: number;
  customer_name: string;
  customer_email: string;
  total_spent: number;
  satisfaction_score: number;
  last_interaction: string;
}

interface Dataset {
  id: number;
  dataset_name: string;
  dataset_slug: string;
  description: string;
  table_name: string;
}

interface DatasetData {
  dataset: Dataset;
  data: any[];
  count: number;
}

interface UserWithPermissions {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  datasets: string[];
}

interface Analytics {
  totalCustomers: number;
  totalRevenue: number;
  avgSatisfaction: string;
}

const Dashboard: React.FC = () => {
  const { user, token, logout } = useAuth();
  const [accessStatus, setAccessStatus] = useState<AccessStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAccessStatus();
  }, []);

  const checkAccessStatus = async () => {
    try {
      const response = await fetch('/vanijya/api/access-status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAccessStatus(data);
    } catch (error) {
      console.error('Failed to check access:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!accessStatus?.hasAccess) {
    if (accessStatus?.status === 'pending') {
      return <PendingAccess user={user!} logout={logout} />;
    } else if (accessStatus?.status === 'denied') {
      return <AccessDenied user={user!} logout={logout} />;
    } else {
      return <RequestAccessForm user={user!} token={token!} logout={logout} onRequestSubmitted={checkAccessStatus} />;
    }
  }

  const isAdmin = accessStatus.role === 'admin';
  
  return isAdmin ? (
    <AdminDashboard user={user!} token={token!} logout={logout} />
  ) : (
    <ViewerDashboard user={user!} token={token!} logout={logout} />
  );
};

// Request Access Form Component
const RequestAccessForm: React.FC<{
  user: any;
  token: string;
  logout: () => void;
  onRequestSubmitted: () => void;
}> = ({ user, token, logout, onRequestSubmitted }) => {
  const [businessName, setBusinessName] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const response = await fetch('/vanijya/api/request-access', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ businessName, reason })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit request');
      }

      onRequestSubmitted();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-900 p-4">
      <div className="max-w-2xl mx-auto pt-20">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl mb-4">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Request Access</h1>
            <p className="text-gray-600 mt-2">Welcome, {user.name}!</p>
            <p className="text-sm text-gray-500 mt-1">Request access to Vanijya Business Analytics Portal</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Name
              </label>
              <input
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="Your Company Name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Access
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="Why do you need access to analytics?"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </form>

          <div className="mt-6 flex justify-between text-sm">
            <a href="/" className="text-gray-600 hover:text-gray-900 flex items-center gap-1">
              <Home className="w-4 h-4" /> Home
            </a>
            <button onClick={logout} className="text-gray-600 hover:text-gray-900 flex items-center gap-1">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Pending/Denied Access Components (same as before)
const PendingAccess: React.FC<{ user: any; logout: () => void }> = ({ user, logout }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-900 p-4 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md text-center">
        <Clock className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Pending</h2>
        <p className="text-gray-600 mb-6">
          Your access request is being reviewed by the admin.
        </p>
        <div className="flex gap-3 justify-center">
          <a href="/" className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
            Go Home
          </a>
          <button onClick={logout} className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600">
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

const AccessDenied: React.FC<{ user: any; logout: () => void }> = ({ user, logout }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-900 p-4 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md text-center">
        <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600 mb-6">
          Your access request was denied. Please contact the administrator.
        </p>
        <div className="flex gap-3 justify-center">
          <a href="/" className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
            Go Home
          </a>
          <button onClick={logout} className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600">
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

// Admin Dashboard with Dataset Management
const AdminDashboard: React.FC<{ user: any; token: string; logout: () => void }> = ({ user, token, logout }) => {
  const [activeTab, setActiveTab] = useState<string>('general');
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [datasetData, setDatasetData] = useState<Record<string, DatasetData>>({});
  const [users, setUsers] = useState<UserWithPermissions[]>([]);
  const [requests, setRequests] = useState<any[]>([]);

  useEffect(() => {
    loadDatasets();
    loadUsers();
    loadAccessRequests();
  }, []);

  const loadDatasets = async () => {
    try {
      const response = await fetch('/vanijya/api/datasets', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDatasets(data.datasets);
      setUserPermissions(data.userPermissions);
    } catch (error) {
      console.error('Failed to load datasets:', error);
    }
  };

  const loadDatasetData = async (slug: string, forceReload: boolean = false) => {
    if (datasetData[slug] && !forceReload) return; // Already loaded

    try {
      console.log('Loading dataset:', slug);
      const response = await fetch(`/vanijya/api/dataset/${slug}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      console.log('Dataset loaded:', slug, data);
      setDatasetData(prev => ({ ...prev, [slug]: data }));
    } catch (error) {
      console.error(`Failed to load dataset ${slug}:`, error);
    }
  };

  const handleDataChange = () => {
    console.log('Data changed, reloading:', activeTab);
    // Force reload the current dataset
    setDatasetData(prev => {
      const newState = { ...prev };
      delete newState[activeTab];
      return newState;
    });
    loadDatasetData(activeTab, true);
  };

  const loadUsers = async () => {
    try {
      const response = await fetch('/vanijya/api/admin/users-with-permissions', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setUsers(data.users);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadAccessRequests = async () => {
    try {
      const response = await fetch('/vanijya/api/admin/access-requests', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setRequests(data.requests);
    } catch (error) {
      console.error('Failed to load requests:', error);
    }
  };

  const handleGrantDatasetPermission = async (userId: number, datasetSlug: string) => {
    try {
      await fetch('/vanijya/api/admin/grant-dataset-permission', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId, datasetSlug })
      });
      loadUsers();
    } catch (error) {
      console.error('Failed to grant permission:', error);
    }
  };

  const handleRevokeDatasetPermission = async (userId: number, datasetSlug: string) => {
    try {
      await fetch('/vanijya/api/admin/revoke-dataset-permission', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId, datasetSlug })
      });
      loadUsers();
    } catch (error) {
      console.error('Failed to revoke permission:', error);
    }
  };

  const handleApprove = async (requestId: number) => {
    try {
      await fetch('/vanijya/api/admin/approve-access', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ requestId })
      });
      loadAccessRequests();
      loadUsers();
    } catch (error) {
      console.error('Failed to approve:', error);
    }
  };

  const handleDeny = async (requestId: number) => {
    try {
      await fetch('/vanijya/api/admin/deny-access', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ requestId })
      });
      loadAccessRequests();
    } catch (error) {
      console.error('Failed to deny:', error);
    }
  };

  // Load dataset data when tab changes
  useEffect(() => {
    if (activeTab !== 'general' && activeTab !== 'requests' && activeTab !== 'users') {
      loadDatasetData(activeTab);
    }
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Building2 className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-bold">Vanijya Admin</h1>
              <p className="text-sm text-indigo-100">Business Analytics Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm">👤 {user.name}</span>
            <a href="/" className="px-3 py-1 bg-white/20 rounded hover:bg-white/30">Home</a>
            <button onClick={logout} className="px-3 py-1 bg-white/20 rounded hover:bg-white/30">Logout</button>
          </div>
        </div>
      </header>

      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-2 overflow-x-auto">
            <button
              onClick={() => setActiveTab('general')}
              className={`px-4 py-3 font-medium border-b-2 whitespace-nowrap ${
                activeTab === 'general' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-600'
              }`}
            >
              📊 General Analytics
            </button>
            {datasets.map(dataset => (
              <button
                key={dataset.dataset_slug}
                onClick={() => setActiveTab(dataset.dataset_slug)}
                className={`px-4 py-3 font-medium border-b-2 whitespace-nowrap ${
                  activeTab === dataset.dataset_slug ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-600'
                }`}
              >
                <Database className="w-4 h-4 inline mr-1" />
                {dataset.dataset_name}
              </button>
            ))}
            <button
              onClick={() => setActiveTab('users')}
              className={`px-4 py-3 font-medium border-b-2 whitespace-nowrap ${
                activeTab === 'users' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-600'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-1" />
              User Permissions
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`px-4 py-3 font-medium border-b-2 whitespace-nowrap ${
                activeTab === 'requests' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-600'
              }`}
            >
              Access Requests
              {requests.filter(r => r.status === 'pending').length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {requests.filter(r => r.status === 'pending').length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'general' && <GeneralAnalytics token={token} />}
        {activeTab === 'requests' && <AccessRequests requests={requests} onApprove={handleApprove} onDeny={handleDeny} />}
        {activeTab === 'users' && (
          <UserPermissionsManagement 
            users={users} 
            datasets={datasets}
            onGrant={handleGrantDatasetPermission}
            onRevoke={handleRevokeDatasetPermission}
          />
        )}
        {activeTab !== 'general' && activeTab !== 'requests' && activeTab !== 'users' && datasetData[activeTab] && (
          <DatasetView
            data={datasetData[activeTab]}
            token={token}
            onDataChange={handleDataChange}
            isAdmin={true}
          />
        )}
      </div>
    </div>
  );
};

// Viewer Dashboard (Regular User)
const ViewerDashboard: React.FC<{ user: any; token: string; logout: () => void }> = ({ user, token, logout }) => {
  const [activeTab, setActiveTab] = useState<string>('');
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [datasetData, setDatasetData] = useState<Record<string, DatasetData>>({});

  useEffect(() => {
    loadDatasets();
  }, []);

  const loadDatasets = async () => {
    try {
      const response = await fetch('/vanijya/api/datasets', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDatasets(data.datasets);
      setUserPermissions(data.userPermissions);
      
      // Set first permitted dataset as active
      if (data.userPermissions.length > 0) {
        setActiveTab(data.userPermissions[0]);
      }
    } catch (error) {
      console.error('Failed to load datasets:', error);
    }
  };

  const loadDatasetData = async (slug: string) => {
    if (datasetData[slug]) return;
    
    try {
      const response = await fetch(`/vanijya/api/dataset/${slug}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDatasetData(prev => ({ ...prev, [slug]: data }));
    } catch (error) {
      console.error(`Failed to load dataset ${slug}:`, error);
    }
  };

  useEffect(() => {
    if (activeTab && userPermissions.includes(activeTab)) {
      loadDatasetData(activeTab);
    }
  }, [activeTab]);

  const permittedDatasets = datasets.filter(d => userPermissions.includes(d.dataset_slug));

  if (permittedDatasets.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8" />
              <h1 className="text-2xl font-bold">Vanijya Analytics</h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm">👤 {user.name}</span>
              <a href="/" className="px-3 py-1 bg-white/20 rounded hover:bg-white/30">Home</a>
              <button onClick={logout} className="px-3 py-1 bg-white/20 rounded hover:bg-white/30">Logout</button>
            </div>
          </div>
        </header>
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <Database className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Datasets Available</h2>
          <p className="text-gray-600">You don't have permission to view any datasets yet. Contact admin for access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Building2 className="w-8 h-8" />
            <h1 className="text-2xl font-bold">Vanijya Analytics</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm">👤 {user.name}</span>
            <a href="/" className="px-3 py-1 bg-white/20 rounded hover:bg-white/30">Home</a>
            <button onClick={logout} className="px-3 py-1 bg-white/20 rounded hover:bg-white/30">Logout</button>
          </div>
        </div>
      </header>

      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-2 overflow-x-auto">
            {permittedDatasets.map(dataset => (
              <button
                key={dataset.dataset_slug}
                onClick={() => setActiveTab(dataset.dataset_slug)}
                className={`px-4 py-3 font-medium border-b-2 whitespace-nowrap ${
                  activeTab === dataset.dataset_slug ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-600'
                }`}
              >
                <Database className="w-4 h-4 inline mr-1" />
                {dataset.dataset_name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {activeTab && datasetData[activeTab] && <DatasetView data={datasetData[activeTab]} />}
      </div>
    </div>
  );
};

// Helper Components
const GeneralAnalytics: React.FC<{ token: string }> = ({ token }) => {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [analyticsRes, customersRes] = await Promise.all([
        fetch('/vanijya/api/analytics-summary', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/vanijya/api/customer-data', { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      const analyticsData = await analyticsRes.json();
      const customersData = await customersRes.json();
      
      setAnalytics(analyticsData);
      setCustomers(customersData.customers);
    } catch (error) {
      console.error('Failed to load general analytics:', error);
    }
  };

  return (
    <>
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <Users className="w-10 h-10 text-indigo-600" />
              <div>
                <p className="text-sm text-gray-600">Total Customers</p>
                <p className="text-2xl font-bold">{analytics.totalCustomers}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <DollarSign className="w-10 h-10 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold">${analytics.totalRevenue.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <Star className="w-10 h-10 text-yellow-600" />
              <div>
                <p className="text-sm text-gray-600">Avg Satisfaction</p>
                <p className="text-2xl font-bold">{analytics.avgSatisfaction}/5.0</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-bold">Customer Data</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Spent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Satisfaction</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Contact</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {customers.map((customer) => (
                <tr key={customer.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">{customer.customer_name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{customer.customer_email}</td>
                  <td className="px-6 py-4 font-semibold">${customer.total_spent.toLocaleString()}</td>
                  <td className="px-6 py-4">
                    <span className="text-yellow-600">★ {customer.satisfaction_score}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {new Date(customer.last_interaction).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

const DatasetView: React.FC<{ data: DatasetData; token?: string; onDataChange?: () => void; isAdmin?: boolean }> = ({ data, token, onDataChange, isAdmin = false }) => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<any>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const editableColumns = ['name', 'address', 'city', 'postcode', 'food_ordered_from', 'notes', 'latitude', 'longitude'];
  const displayColumns = ['sr_no', ...editableColumns];

  const handleEdit = (row: any) => {
    console.log('Editing row:', row);
    setEditingId(row.id);
    setEditForm({ ...row });
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
    setError(null);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    setError(null);
    console.log('Saving edit:', editForm);

    try {
      const response = await fetch(`/vanijya/api/admin/dataset/${data.dataset.dataset_slug}/row/${editingId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      const result = await response.json();
      console.log('Edit response:', result);

      if (!response.ok) {
        throw new Error(result.error || 'Failed to save');
      }

      setEditingId(null);
      setEditForm({});
      if (onDataChange) onDataChange();
    } catch (err: any) {
      console.error('Save error:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this row?')) return;

    console.log('Deleting row:', id);
    setError(null);

    try {
      const response = await fetch(`/vanijya/api/admin/dataset/${data.dataset.dataset_slug}/row/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();
      console.log('Delete response:', result);

      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete');
      }

      if (onDataChange) onDataChange();
    } catch (err: any) {
      console.error('Delete error:', err);
      setError(err.message);
    }
  };

  const handleAddNew = () => {
    setShowAddForm(true);
    setAddForm({
      name: '',
      address: '',
      city: '',
      postcode: '',
      food_ordered_from: '',
      notes: '',
      latitude: '',
      longitude: ''
    });
    setError(null);
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setAddForm({});
    setError(null);
  };

  const handleSaveAdd = async () => {
    setSaving(true);
    setError(null);
    console.log('Adding new row:', addForm);

    try {
      const response = await fetch(`/vanijya/api/admin/dataset/${data.dataset.dataset_slug}/row`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(addForm)
      });

      const result = await response.json();
      console.log('Add response:', result);

      if (!response.ok) {
        throw new Error(result.error || 'Failed to add');
      }

      setShowAddForm(false);
      setAddForm({});
      if (onDataChange) onDataChange();
    } catch (err: any) {
      console.error('Add error:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!data || !data.data) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
        Loading data...
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b flex justify-between items-start">
        <div>
          <h2 className="text-xl font-bold">{data.dataset.dataset_name}</h2>
          <p className="text-sm text-gray-600 mt-1">{data.dataset.description}</p>
          <p className="text-sm text-gray-500 mt-1">Total records: {data.count}</p>
        </div>
        {isAdmin && (
          <button
            onClick={handleAddNew}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-2"
          >
            + Add New Row
          </button>
        )}
      </div>

      {error && (
        <div className="px-6 py-3 bg-red-50 border-b border-red-200 text-red-700">
          Error: {error}
        </div>
      )}

      {/* Add New Form */}
      {showAddForm && (
        <div className="px-6 py-4 bg-green-50 border-b">
          <h3 className="font-semibold mb-3">Add New Row</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {editableColumns.map(col => (
              <div key={col}>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {col.replace(/_/g, ' ')}
                </label>
                <input
                  type={col === 'latitude' || col === 'longitude' ? 'number' : 'text'}
                  step={col === 'latitude' || col === 'longitude' ? '0.000001' : undefined}
                  value={addForm[col] || ''}
                  onChange={(e) => setAddForm({ ...addForm, [col]: e.target.value })}
                  className="w-full px-2 py-1 border rounded text-sm"
                  placeholder={col === 'latitude' ? '55.8642' : col === 'longitude' ? '-4.2518' : ''}
                />
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={handleSaveAdd}
              disabled={saving}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleCancelAdd}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {isAdmin && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>}
              {displayColumns.map(col => (
                <th key={col} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {col.replace(/_/g, ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.data.length === 0 ? (
              <tr>
                <td colSpan={displayColumns.length + (isAdmin ? 1 : 0)} className="px-6 py-8 text-center text-gray-500">
                  No data yet. {isAdmin && 'Click "Add New Row" to add data.'}
                </td>
              </tr>
            ) : (
              data.data.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  {isAdmin && (
                    <td className="px-4 py-2">
                      {editingId === row.id ? (
                        <div className="flex gap-1">
                          <button
                            onClick={handleSaveEdit}
                            disabled={saving}
                            className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 disabled:opacity-50"
                          >
                            {saving ? '...' : 'Save'}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="px-2 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleEdit(row)}
                            className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(row.id)}
                            className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                  {displayColumns.map(col => (
                    <td key={col} className="px-4 py-2 text-sm">
                      {editingId === row.id && editableColumns.includes(col) ? (
                        <input
                          type={col === 'latitude' || col === 'longitude' ? 'number' : 'text'}
                          step={col === 'latitude' || col === 'longitude' ? '0.000001' : undefined}
                          value={editForm[col] || ''}
                          onChange={(e) => setEditForm({ ...editForm, [col]: e.target.value })}
                          className="w-full px-2 py-1 border rounded text-sm"
                        />
                      ) : (
                        row[col] || '-'
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AccessRequests: React.FC<{
  requests: any[];
  onApprove: (id: number) => void;
  onDeny: (id: number) => void;
}> = ({ requests, onApprove, onDeny }) => {
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b">
        <h2 className="text-xl font-bold">Access Requests</h2>
      </div>
      <div className="divide-y">
        {requests.map((request) => (
          <div key={request.id} className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold text-lg">{request.user_name}</h3>
                <p className="text-sm text-gray-600">{request.user_email}</p>
                <p className="text-sm text-gray-500 mt-1">Business: {request.business_name || 'N/A'}</p>
                <p className="text-sm text-gray-700 mt-2">Reason: {request.reason || 'N/A'}</p>
                <p className="text-xs text-gray-400 mt-2">
                  Requested: {new Date(request.requested_at).toLocaleString()}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <span className={`px-3 py-1 rounded text-sm font-semibold ${
                  request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  request.status === 'approved' ? 'bg-green-100 text-green-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {request.status.toUpperCase()}
                </span>
                {request.status === 'pending' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => onApprove(request.id)}
                      className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => onDeny(request.id)}
                      className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                    >
                      Deny
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        {requests.length === 0 && (
          <div className="p-6 text-center text-gray-500">No access requests</div>
        )}
      </div>
    </div>
  );
};

const UserPermissionsManagement: React.FC<{
  users: UserWithPermissions[];
  datasets: Dataset[];
  onGrant: (userId: number, datasetSlug: string) => void;
  onRevoke: (userId: number, datasetSlug: string) => void;
}> = ({ users, datasets, onGrant, onRevoke }) => {
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b">
        <h2 className="text-xl font-bold">Manage User Dataset Permissions</h2>
        <p className="text-sm text-gray-600 mt-1">Grant or revoke access to specific datasets for each user</p>
      </div>
      <div className="divide-y">
        {users.map((user) => (
          <div key={user.id} className="p-6">
            <div className="mb-4">
              <h3 className="font-semibold text-lg">{user.user_name}</h3>
              <p className="text-sm text-gray-600">{user.user_email}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Dataset Access:</p>
              <div className="flex flex-wrap gap-2">
                {datasets.map(dataset => {
                  const hasAccess = user.datasets.includes(dataset.dataset_slug);
                  return (
                    <button
                      key={dataset.dataset_slug}
                      onClick={() => hasAccess 
                        ? onRevoke(user.user_id, dataset.dataset_slug)
                        : onGrant(user.user_id, dataset.dataset_slug)
                      }
                      className={`px-3 py-1 rounded text-sm font-medium ${
                        hasAccess 
                          ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {hasAccess ? '✓ ' : ''}{dataset.dataset_name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
        {users.length === 0 && (
          <div className="p-6 text-center text-gray-500">No approved users yet</div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
